import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { FilterBar } from '../../components/FilterBar';
import { StatsCards } from '../../components/StatsCards';
import { galerieService, GalerieImage } from '../../services/galerieService';
import api from '../../config/api';

export const AdminGalerie: React.FC = () => {
  const [allImages, setAllImages] = useState<GalerieImage[]>([]);
  const [images, setImages] = useState<GalerieImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalerieImage | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sectionStats, setSectionStats] = useState<{ VCN: number; HDR: number; SANTE: number }>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    categorie: '',
    section: '',
  });

  useEffect(() => {
    fetchImages(1);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allImages, currentPage]);

  const fetchImages = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await galerieService.getAll(1, 1000);
      const allImagesData = data.results || [];
      setAllImages(allImagesData);
      setTotalCount(allImagesData.length);

      const sectionCounts = { VCN: 0, HDR: 0, SANTE: 0 };
      allImagesData.forEach((image: GalerieImage) => {
        if (image.section === 'VCN') {
          sectionCounts.VCN += 1;
        } else if (image.section === 'HDR') {
          sectionCounts.HDR += 1;
        } else if (image.section === 'SANTE') {
          sectionCounts.SANTE += 1;
        }
      });
      setSectionStats(sectionCounts);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allImages];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((image) =>
        image.titre?.toLowerCase().includes(searchLower) ||
        image.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par catégorie
    if (filters.categorie) {
      filtered = filtered.filter((image) => image.categorie === filters.categorie);
    }

    // Filtre par section
    if (filters.section) {
      filtered = filtered.filter((image) => (image as any).section === filters.section);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 6;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedImages = filtered.slice(startIndex, endIndex);

    setImages(paginatedImages);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({ categorie: 'autre', section: 'VCN' });
    setSelectedImage(null);
    setIsModalOpen(true);
  };

  const handleEdit = (image: GalerieImage) => {
    setFormData({
      titre: image.titre,
      description: image.description || '',
      categorie: image.categorie,
      section: image.section || 'VCN',
      image: null, // Ne pas précharger le fichier
    });
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const handleView = (image: GalerieImage) => {
    setSelectedImage(image);
    setIsViewModalOpen(true);
  };

  const handleDelete = (image: GalerieImage) => {
    setSelectedImage(image);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'image' && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      if (selectedImage) {
        await api.put(`/galerie/images/${selectedImage.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/galerie/images/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsModalOpen(false);
      setFormData({});
      setSelectedImage(null);
      await fetchImages(currentPage);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const confirmDelete = async () => {
    if (!selectedImage) return;
    try {
      await api.delete(`/galerie/images/${selectedImage.id}/`);
      setIsDeleteModalOpen(false);
      await fetchImages(currentPage);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const getCategorieLabel = (cat: string) => {
    const labels: { [key: string]: string } = {
      membres: '👥 Membres',
      bureau: '💼 Bureau',
      serigne: '📿 Serigne',
      khlifa: '🕌 Khlifa',
      autre: '📌 Autre',
    };
    return labels[cat] || cat;
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'titre', 
      label: 'Titre',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    {
      key: 'categorie',
      label: 'Catégorie',
      render: (value: string) => (
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
          {getCategorieLabel(value)}
        </span>
      )
    },
    {
      key: 'section',
      label: 'Section',
      render: (value: string) => {
        const sections: { [key: string]: string } = {
          VCN: 'VCN (Université)',
          HDR: 'HDR',
          SANTE: 'SANTÉ',
        };
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            {sections[value] || value}
          </span>
        );
      }
    },
    {
      key: 'nombre_vues',
      label: 'Vues',
      render: (value: number) => (
        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">
          👁️ {value}
        </span>
      )
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="galerie">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="galerie">
      <div className="flex justify-between items-center mb-6 bg-white rounded-2xl shadow-lg p-6">
        <div>
          <h2 className="text-3xl font-bold mb-2 text-green-700">Gestion de la Galerie</h2>
          <p className="text-gray-600 mt-1">Créez et gérez les images de la galerie</p>
        </div>
        <button onClick={handleCreate} className="btn-primary text-lg px-6 py-3">
          + Nouvelle Image
        </button>
      </div>

      <StatsCards
        stats={[
          { label: 'Total Images', value: totalCount, icon: '🖼️', color: 'green' },
          { label: 'Section VCN', value: sectionStats.VCN, icon: '🎓', color: 'blue' },
          { label: 'Section HDR', value: sectionStats.HDR, icon: '🏛️', color: 'purple' },
          { label: 'Section SANTÉ', value: sectionStats.SANTE, icon: '🩺', color: 'teal' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder="🔍 Rechercher par titre, description..."
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          setCurrentPage(1);
        }}
        filters={[
          {
            key: 'categorie',
            label: 'Toutes les catégories',
            value: filters.categorie,
            onChange: (value) => {
              setFilters({ ...filters, categorie: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'membres', label: '👥 Membres' },
              { value: 'bureau', label: '💼 Bureau' },
              { value: 'serigne', label: '📿 Serigne' },
              { value: 'khlifa', label: '🕌 Khlifa' },
              { value: 'autre', label: '📌 Autre' },
            ],
          },
          {
            key: 'section',
            label: 'Toutes les sections',
            value: filters.section,
            onChange: (value) => {
              setFilters({ ...filters, section: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'VCN', label: '🎓 VCN (Université)' },
              { value: 'HDR', label: '🏛️ HDR' },
              { value: 'SANTE', label: '🩺 SANTÉ' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ search: '', categorie: '', section: '' });
          setCurrentPage(1);
        }}
      />

      <Table
        columns={columns}
        data={images}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        loading={loading}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedImage ? 'Modifier l\'image' : 'Nouvelle image'}
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleSubmit} className="btn-primary">
              Enregistrer
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              className="input-field"
              value={formData.titre || ''}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="input-field min-h-[100px]"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
            <select
              className="input-field"
              value={formData.categorie || ''}
              onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
              required
            >
              <option value="">Sélectionner</option>
              <option value="membres">👥 Membres</option>
              <option value="bureau">💼 Bureau</option>
              <option value="serigne">📿 Serigne</option>
              <option value="khlifa">🕌 Khlifa</option>
              <option value="autre">📌 Autre</option>
            </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Hanafia *</label>
              <select
                className="input-field"
                value={formData.section || 'VCN'}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                required
              >
                <option value="VCN">VCN (Université)</option>
                <option value="HDR">HDR</option>
                <option value="SANTE">SANTÉ</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image {selectedImage && selectedImage.image && '(actuelle)'} {!selectedImage && '*'}
            </label>
            <div className="flex items-center space-x-4">
              {selectedImage && selectedImage.image && (
                <div className="flex-shrink-0">
                  <img 
                    src={selectedImage.image} 
                    alt="Image actuelle"
                    className="w-32 h-32 object-cover rounded-lg border-2 border-green-300 shadow-md"
                  />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  className="input-field"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFormData({ ...formData, image: e.target.files[0] });
                    }
                  }}
                  required={!selectedImage}
                />
                <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG, GIF (max 5MB)</p>
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Détails de l'image"
      >
        {selectedImage && (
          <div className="space-y-4">
            <img
              src={selectedImage.image}
              alt={selectedImage.titre}
              className="w-full h-64 object-cover rounded-xl"
            />
            <div>
              <p className="text-sm text-gray-600">Titre</p>
              <p className="font-semibold text-lg">{selectedImage.titre}</p>
            </div>
            {selectedImage.description && (
              <div>
                <p className="text-sm text-gray-600">Description</p>
                <p className="font-semibold">{selectedImage.description}</p>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Catégorie</p>
                <p className="font-semibold">{getCategorieLabel(selectedImage.categorie)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vues</p>
                <p className="font-semibold">👁️ {selectedImage.nombre_vues}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={confirmDelete} className="btn-danger">
              Supprimer
            </button>
          </div>
        }
      >
        <p className="text-lg">Êtes-vous sûr de vouloir supprimer l'image <strong>"{selectedImage?.titre}"</strong> ?</p>
      </Modal>
    </AdminLayout>
  );
};

